import React from 'react';
import { MapPoint, PointPickerMap } from '../map/PointPickerMap';
import { AddressByPoint } from '../../utils/addressByPoint';
import { TranslationSchema } from '../../locales';
import { useLanguage } from '../../contexts/LanguageContext';
import './auth.css';

/** Адрес по точке: ещё не искали, ищем или уже знаем, что стало с адресом. */
export type AddressLookup = { status: 'idle' } | { status: 'loading' } | { status: 'done'; result: AddressByPoint };

interface VenuePointPickerProps {
  isOpen: boolean;
  onOpen: () => void;
  point: MapPoint | null;
  center: MapPoint;
  zoom: number;
  focusKey: number;
  onPick: (point: MapPoint) => void;
  lookup: AddressLookup;
  disabled?: boolean;
}

interface LookupMessage {
  text: string;
  /** `warn` — партнёру надо что-то дописать в адресе. */
  tone: 'info' | 'warn';
}

/**
 * Адрес оставлен — почему. Свой номер дома партнёр знает лучше соседа,
 * найденного по точке; а подстановку по прошлой точке надо проверить.
 */
function keptMessage(result: Extract<AddressByPoint, { status: 'kept' }>, strings: TranslationSchema['auth']): string {
  if (!result.typed) {
    return `${strings.mapAddressKeptPre} «${result.found}» ${strings.mapAddressKeptEarlierPost}`;
  }
  if (result.foundPrecision === 'house') {
    return `${strings.mapAddressFoundPre} «${result.found}» ${strings.mapAddressKeptHousePost}`;
  }
  return `${strings.mapAddressKeptPre} «${result.found}» ${strings.mapAddressKeptPost}`;
}

function lookupMessage(lookup: AddressLookup, strings: TranslationSchema['auth']): LookupMessage | null {
  if (lookup.status === 'idle') return null;
  if (lookup.status === 'loading') return { text: strings.mapLookupLoading, tone: 'info' };

  const { result } = lookup;
  switch (result.status) {
    case 'house':
      return { text: strings.mapAddressHouse, tone: 'info' };
    case 'street':
      return { text: strings.mapAddressStreet, tone: 'warn' };
    case 'kept':
      return { text: keptMessage(result, strings), tone: result.typed ? 'info' : 'warn' };
    case 'none':
      return { text: strings.mapAddressNone, tone: 'warn' };
  }
}

function PinIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <path d="M12 21s-7-7.5-7-12a7 7 0 0114 0c0 4.5-7 12-7 12z" strokeLinejoin="round" />
      <circle cx="12" cy="9" r="2.5" />
    </svg>
  );
}

/**
 * Адрес заведения по карте: партнёр ставит точку на здание, адрес
 * подставляется сам (ADR-025).
 *
 * Карта спрятана за кнопкой: многие вписывают адрес руками, и анкета не
 * должна расти ради тех, кому удобнее карта. Точка необязательна — без неё
 * анкета уходит как раньше, а поставить её можно и позже, в кабинете.
 */
export function VenuePointPicker({
  isOpen,
  onOpen,
  point,
  center,
  zoom,
  focusKey,
  onPick,
  lookup,
  disabled,
}: VenuePointPickerProps) {
  const { t } = useLanguage();

  if (!isOpen) {
    return (
      <button type="button" className="m3-btn-tonal qp-auth-map-open" onClick={onOpen} disabled={disabled}>
        <PinIcon />
        {t.auth.mapPickButton}
      </button>
    );
  }

  const message = lookupMessage(lookup, t.auth);

  return (
    <div className="qp-auth-map">
      <p className="qp-auth-map-hint">{t.auth.mapPickHint}</p>
      <PointPickerMap
        point={point}
        center={center}
        zoom={zoom}
        focusKey={focusKey}
        onPick={onPick}
        height={280}
        testId="registration-map"
      />
      <div className="qp-auth-map-status" role="status" aria-live="polite">
        {message && <span className={`qp-auth-map-message qp-auth-map-message--${message.tone}`}>{message.text}</span>}
        {point && (
          <span className="qp-auth-map-coords">
            {`${t.auth.mapPointLabel}: ${point.latitude.toFixed(6)}, ${point.longitude.toFixed(6)}`}
          </span>
        )}
      </div>
    </div>
  );
}
